import { Map } from 'yandex-maps';
import { SleeveManager } from '@lib/sleeve-manager';
import { $zoomModeEnabled } from '@widgets/storage-viewer/model';
import type { SleeveDescription } from '@lib/sleeve-manager';
import { DiContainer } from '@@/di/di-container';
import { ClientStorageService } from '@@/common/services/client-storage.service';
import hardRoute from './freight_route-1718954870431.json';

const DEFAULT_VIEWER_SETTINGS = {
  center: [45.04484, 38.97603],
  zoom: 8,
  zoom_default: 8,
  controls: ['zoomControl', 'fullscreenControl'],
};

const VIEWER_SETTINGS = 'VIEWER_SETTINGS';

const clientStorageService = DiContainer.get(ClientStorageService);

class PointsMap {
  private _map?: Map;
  private _containerId?: string;
  private _viewerSettings: typeof DEFAULT_VIEWER_SETTINGS = DEFAULT_VIEWER_SETTINGS;
  private _savedSettings: {
    type: ymaps.MapType;
    zoom: number;
    position: number[];
  } | null = null;
  private _sleeveManager: SleeveManager<SleeveDescription[]> | null = null;
  private _polyline?: ymaps.Polyline;
  private _container: any;

  private initViewerSettings() {
    try {
      const storedViewerSettings = clientStorageService.get<string>(VIEWER_SETTINGS);

      this._viewerSettings = storedViewerSettings
        ? JSON.parse(storedViewerSettings)
        : DEFAULT_VIEWER_SETTINGS;
      clientStorageService.set(VIEWER_SETTINGS, this._viewerSettings);
    } catch (error) {
      this._viewerSettings = DEFAULT_VIEWER_SETTINGS;
    }
    if (!$zoomModeEnabled.getState() && this._viewerSettings.zoom_default) {
      this._viewerSettings.zoom = this._viewerSettings.zoom_default;
    }
  }

  private updateViewerSettings = () => {
    const center = this._map?.getCenter() ?? DEFAULT_VIEWER_SETTINGS.center;
    const zoom = this._map?.getZoom() ?? DEFAULT_VIEWER_SETTINGS.zoom;
    const settings = { ...this._viewerSettings, center, zoom };
    if (!$zoomModeEnabled.getState()) {
      settings.zoom_default = zoom;
    }
    clientStorageService.set(VIEWER_SETTINGS, settings);
  };

  init(id: string, container: HTMLElement | null) {
    if (!container) throw new Error('Не задан container');

    this._container = container;
    this._containerId = id;

    this.initViewerSettings();

    const map = new ymaps.Map(this._containerId, this._viewerSettings);

    this._map = map;

    map.events.add('actionend', this.updateViewerSettings);

    map.behaviors.disable('dblClickZoom');

    const geoLocationControl = new ymaps.control.GeolocationControl();
    map.controls.add(geoLocationControl);

    const viewTypeSelector = new ymaps.control.TypeSelector();
    map.controls.add(viewTypeSelector);
  }

  dispose() {
    if (this._map) {
      // console.log('Удаляем карту ...');
      this._map.destroy();
    }

    if (this._sleeveManager) this._sleeveManager.dispose();
  }

  resize() {
    // console.log('resize');
    if (this._map) this._map.container.fitToViewport();
  }

  setZoomToView() {
    if (this._map) {
      const currentZoom = this._map.getZoom();
      const newZoom = currentZoom * 2.3;
      this._map.setZoom(newZoom);
    }
  }

  setViewMode(mode: 'scheme' | 'satellite' | 'hybrid') {
    if (mode === 'scheme') {
      this._map?.setType('yandex#map');
    } else if (mode === 'hybrid') {
      this._map?.setType('yandex#hybrid');
    } else if (mode === 'satellite') {
      this._map?.setType('yandex#satellite');
    }
  }

  drawRoute(route: number[][]) {
    const startMarker = new ymaps.Placemark(
      route[0],
      { iconCaption: 'Начальная точка'},
      { iconColor: 'rgb(231, 58, 66)' },
    );
    this._map?.geoObjects.add(startMarker);
    const endMarker = new ymaps.Placemark(
      route[route.length - 1],
      { iconCaption: 'Конечная точка' },
      { iconColor: 'rgb(231, 58, 66)' },
    );
    this._map?.geoObjects.add(endMarker);
    this._polyline = new ymaps.Polyline(
      route,
      {},
      { strokeColor: '#ad42f5', strokeWidth: 2 },
    );
    this._map?.geoObjects.add(this._polyline);

    const bounds = this._polyline.geometry?.getBounds();
    if (bounds) {
      this._map?.setBounds(bounds, { checkZoomRange: true }).then(() => {
        const mapCenter = this._map?.getCenter();
        let mapZoom = this._map?.getZoom();
        if (mapZoom) {
          mapZoom = mapZoom - 1;
          this._map?.setZoom(mapZoom);
        }
        if (mapZoom && mapCenter) {
          this._viewerSettings = {
            ...DEFAULT_VIEWER_SETTINGS,
            center: mapCenter,
            zoom: mapZoom,
          };
        }
      });
    }

    return;
  }
}

export const pointsMap = new PointsMap();

var lightRoute = [
  [47.284171, 39.673509],
  [47.284172, 39.673437],
  [47.284206, 39.672361],
  [47.284218, 39.672046],
  [47.284241, 39.671229],
  [47.284268, 39.670437],
  [47.284275, 39.670289],
  [47.284287, 39.670161],
  [47.284305, 39.669991],
  [47.284316, 39.669907],
  [47.284338, 39.669757],
  [47.284361, 39.669636],
  [47.284392, 39.669484],
  [47.284432, 39.669306],
  [47.284474, 39.669154],
  [47.284511, 39.669035],
  [47.284577, 39.668848],
  [47.284638, 39.668685],
  [47.284693, 39.668555],
  [47.284757, 39.668417],
  [47.284838, 39.668251],
  [47.284891, 39.668157],
  [47.284977, 39.66802],
  [47.285026, 39.667947],
  [47.285092, 39.667862],
  [47.28521, 39.667714],
  [47.285313, 39.667592],
  [47.285408, 39.667489],
  [47.285595, 39.667304],
  [47.285712, 39.667193],
  [47.28587, 39.667047],
  [47.285911, 39.667009],
  [47.286127, 39.666814],
  [47.286416, 39.666545],
  [47.286663, 39.666315],
  [47.286904, 39.666094],
  [47.287183, 39.665833],
  [47.287416, 39.665617],
  [47.287763, 39.665297],
  [47.287875, 39.665194],
  [47.288049, 39.665036],
  [47.288211, 39.664883],
  [47.288428, 39.664688],
  [47.288657, 39.664475],
  [47.288833, 39.664312],
  [47.289142, 39.664031],
  [47.28952, 39.663693],
  [47.289659, 39.663559],
  [47.289757, 39.663473],
  [47.290028, 39.66322],
  [47.290152, 39.663104],
  [47.290333, 39.662936],
  [47.290631, 39.662665],
  [47.290911, 39.662406],
  [47.291113, 39.662221],
  [47.291602, 39.661772],
  [47.291847, 39.661542],
  [47.291881, 39.661514],
  [47.292102, 39.661315],
  [47.292295, 39.661152],
  [47.292463, 39.661002],
  [47.292636, 39.660888],
  [47.292728, 39.660829],
  [47.292728, 39.660829],
  [47.293206, 39.660547],
  [47.293357, 39.660461],
  [47.293656, 39.660294],
  [47.293812, 39.660204],
  [47.29387, 39.660161],
  [47.293908, 39.66012],
  [47.293947, 39.660072],
  [47.293944, 39.659985],
  [47.293937, 39.659935],
  [47.293926, 39.65989],
  [47.293907, 39.65984],
  [47.293884, 39.6598],
  [47.29385, 39.65976],
  [47.293789, 39.659766],
  [47.293738, 39.659774],
  [47.293696, 39.659785],
  [47.293659, 39.659798],
  [47.293509, 39.659869],
  [47.293123, 39.660078],
  [47.2928, 39.660255],
  [47.292617, 39.660357],
  [47.29257, 39.660385],
  [47.29257, 39.660385],
  [47.292469, 39.66044],
  [47.292354, 39.660516],
  [47.29223, 39.660625],
  [47.292187, 39.660652],
  [47.292149, 39.660674],
  [47.292059, 39.660713],
  [47.29197, 39.660745],
  [47.291787, 39.660805],
  [47.291708, 39.660823],
  [47.291039, 39.660963],
  [47.290793, 39.661017],
  [47.290468, 39.661086],
  [47.290213, 39.661137],
  [47.290066, 39.661169],
  [47.289431, 39.661306],
  [47.28914, 39.661366],
  [47.288835, 39.661434],
  [47.288835, 39.661434],
  [47.28882, 39.661314],
  [47.288712, 39.660537],
  [47.288701, 39.660462],
  [47.288657, 39.660032],
  [47.288625, 39.659691],
  [47.288608, 39.659612],
  [47.288561, 39.659034],
  [47.288533, 39.658707],
  [47.288507, 39.658428],
  [47.288447, 39.65784],
  [47.288428, 39.657646],
  [47.288368, 39.656919],
  [47.288365, 39.656827],
  [47.288364, 39.656754],
  [47.288369, 39.656655],
  [47.288375, 39.656578],
  [47.288382, 39.656515],
  [47.288407, 39.656362],
  [47.288421, 39.656227],
  [47.288421, 39.656227],
  [47.288591, 39.656073],
  [47.288591, 39.656073],
  [47.288563, 39.655964],
  [47.28853, 39.655869],
  [47.288366, 39.655409],
  [47.288314, 39.655276],
  [47.288226, 39.655052],
  [47.287722, 39.6537],
  [47.287292, 39.65257],
  [47.286956, 39.651661],
  [47.286823, 39.65131],
  [47.286823, 39.65131],
  [47.286698, 39.651415],
  [47.286569, 39.651517],
  [47.286298, 39.65175],
  [47.286243, 39.651794],
  [47.286056, 39.651935],
  [47.286006, 39.651983],
  [47.28598, 39.652018],
  [47.285961, 39.65205],
  [47.285947, 39.65209],
  [47.28594, 39.652121],
  [47.285934, 39.652152],
  [47.285928, 39.652225],
  [47.285926, 39.652256],
  [47.285925, 39.652281],
  [47.28593, 39.65233],
  [47.285956, 39.652446],
  [47.285983, 39.652551],
  [47.28618, 39.653222],
  [47.286236, 39.653389],
  [47.286401, 39.653824],
  [47.28654, 39.654209],
  [47.286607, 39.654369],
  [47.286692, 39.654584],
  [47.28688, 39.655099],
  [47.287193, 39.655973],
];
